import * as THREE from 'three';
import { Node, Beam } from '../types/nnstructural2D';

/**
 * Selection validation utilities for 100% containment checking
 * These functions work alongside existing selection logic without modifying it
 */

export interface SelectionBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface ElementBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/**
 * Get the complete bounds of a node element (including visual size)
 */
export function getNodeBounds(
  node: Node,
  camera: THREE.Camera,
  canvasRect: DOMRect
): ElementBounds {
  const worldPosition = new THREE.Vector3(node.x, node.y, node.z);
  const screenPosition = projectToScreen(worldPosition, camera, canvasRect);
  
  // Node visual size in screen pixels (adjust based on your node rendering)
  const nodeRadius = 8; // 8px radius for node circle
  
  return {
    left: screenPosition.x - nodeRadius,
    right: screenPosition.x + nodeRadius,
    top: screenPosition.y - nodeRadius,
    bottom: screenPosition.y + nodeRadius
  };
}

/**
 * Get the complete bounds of a beam element (including visual thickness)
 */
export function getBeamBounds(
  beam: Beam,
  nodes: Node[],
  camera: THREE.Camera,
  canvasRect: DOMRect
): ElementBounds {
  const startNode = nodes.find(n => n.id === beam.startNodeId);
  const endNode = nodes.find(n => n.id === beam.endNodeId);
  
  if (!startNode || !endNode) {
    return { left: 0, right: 0, top: 0, bottom: 0 };
  }
  
  const startWorld = new THREE.Vector3(startNode.x, startNode.y, startNode.z);
  const endWorld = new THREE.Vector3(endNode.x, endNode.y, endNode.z);
  
  const startScreen = projectToScreen(startWorld, camera, canvasRect);
  const endScreen = projectToScreen(endWorld, camera, canvasRect);
  
  // Beam visual thickness in screen pixels (adjust based on your beam rendering)
  const beamThickness = 4; // 4px thickness for beam line
  
  // Calculate beam bounds including thickness
  const minX = Math.min(startScreen.x, endScreen.x) - beamThickness;
  const maxX = Math.max(startScreen.x, endScreen.x) + beamThickness;
  const minY = Math.min(startScreen.y, endScreen.y) - beamThickness;
  const maxY = Math.max(startScreen.y, endScreen.y) + beamThickness;
  
  return {
    left: minX,
    right: maxX,
    top: minY,
    bottom: maxY
  };
}

/**
 * Check if an element is COMPLETELY contained within the selection bounds
 * This implements the exact STAAD.Pro behavior
 */
export function isElementCompletelySelected(
  element: Node | Beam,
  nodes: Node[],
  selectionBounds: SelectionBounds,
  camera: THREE.Camera,
  canvasRect: DOMRect
): boolean {
  let elementBounds: ElementBounds;
  
  // Get element bounds based on type
  if ('x' in element && 'y' in element && 'z' in element) {
    // It's a node
    elementBounds = getNodeBounds(element, camera, canvasRect);
  } else if ('startNodeId' in element && 'endNodeId' in element) {
    // It's a beam
    elementBounds = getBeamBounds(element, nodes, camera, canvasRect);
  } else {
    return false;
  }
  
  // Check if element is COMPLETELY within selection bounds
  // This is the key difference from intersection logic
  return (
    elementBounds.left >= selectionBounds.minX &&
    elementBounds.right <= selectionBounds.maxX &&
    elementBounds.top >= selectionBounds.minY &&
    elementBounds.bottom <= selectionBounds.maxY
  );
}

/**
 * Check if a node is 100% contained within the selection bounds
 */
export function isNodeFullyContained(
  node: Node,
  selectionBounds: SelectionBounds,
  camera: THREE.Camera,
  canvasRect: DOMRect
): boolean {
  return isElementCompletelySelected(node, [], selectionBounds, camera, canvasRect);
}

/**
 * Check if a beam is 100% contained within the selection bounds
 */
export function isBeamFullyContained(
  beam: Beam,
  nodes: Node[],
  selectionBounds: SelectionBounds,
  camera: THREE.Camera,
  canvasRect: DOMRect
): boolean {
  return isElementCompletelySelected(beam, nodes, selectionBounds, camera, canvasRect);
}

/**
 * Project 3D world coordinates to 2D screen coordinates
 */
function projectToScreen(
  worldPosition: THREE.Vector3,
  camera: THREE.Camera,
  canvasRect: DOMRect
): { x: number; y: number } {
  const projected = worldPosition.clone().project(camera);
  
  // Convert from normalized device coordinates (-1 to 1) to screen coordinates
  const x = (projected.x + 1) * canvasRect.width / 2 + canvasRect.left;
  const y = (-projected.y + 1) * canvasRect.height / 2 + canvasRect.top;
  
  return { x, y };
}

/**
 * Validate that all selected elements are 100% contained for copy/paste operations
 */
export function validateSelectionForCopyPaste(
  selectedNodeIds: string[],
  selectedBeamIds: string[],
  nodes: Node[],
  beams: Beam[],
  selectionBounds: SelectionBounds,
  camera: THREE.Camera,
  canvasRect: DOMRect
): { isValid: boolean; reason?: string } {
  // Check if any selected nodes are not fully contained
  for (const nodeId of selectedNodeIds) {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) continue;
    
    if (!isNodeFullyContained(node, selectionBounds, camera, canvasRect)) {
      return {
        isValid: false,
        reason: `Node ${nodeId} is not completely contained within selection area`
      };
    }
  }
  
  // Check if any selected beams are not fully contained
  for (const beamId of selectedBeamIds) {
    const beam = beams.find(b => b.id === beamId);
    if (!beam) continue;
    
    if (!isBeamFullyContained(beam, nodes, selectionBounds, camera, canvasRect)) {
      return {
        isValid: false,
        reason: `Beam ${beamId} is not completely contained within selection area`
      };
    }
  }
  
  return { isValid: true };
}

/**
 * Get elements that are completely selected (for copy/paste operations)
 */
export function getCompletelySelectedElements(
  selectedNodeIds: string[],
  selectedBeamIds: string[],
  nodes: Node[],
  beams: Beam[],
  selectionBounds: SelectionBounds,
  camera: THREE.Camera,
  canvasRect: DOMRect
): { nodes: string[], beams: string[] } {
  const completelySelectedNodes: string[] = [];
  const completelySelectedBeams: string[] = [];
  
  // Check nodes
  for (const nodeId of selectedNodeIds) {
    const node = nodes.find(n => n.id === nodeId);
    if (node && isNodeFullyContained(node, selectionBounds, camera, canvasRect)) {
      completelySelectedNodes.push(nodeId);
    }
  }
  
  // Check beams
  for (const beamId of selectedBeamIds) {
    const beam = beams.find(b => b.id === beamId);
    if (beam && isBeamFullyContained(beam, nodes, selectionBounds, camera, canvasRect)) {
      completelySelectedBeams.push(beamId);
    }
  }
  
  return {
    nodes: completelySelectedNodes,
    beams: completelySelectedBeams
  };
} 